'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiResponse,
  MIN_SEARCH_LENGTH,
  PAGE_SIZE,
  PostData,
  SortOption,
} from './types';

interface UseResourceLibraryArgs {
  initialPosts: PostData[];
  initialHasMore: boolean;
  /**
   * Category UID to pre-select on first render (from a `?category=` deep-link /
   * the legacy category-UID redirect). Server-provided so it is identical on the
   * server and the client (no hydration mismatch); the server-seeded first page
   * is expected to already be filtered by it.
   */
  initialCategory?: string;
  /** Search term to pre-fill on first render (from a `?search=` deep-link). */
  initialSearch?: string;
}

/**
 * Owns all client state for the resource library: active filters, debounced
 * search, sort, pagination and the fetching lifecycle. Variants render the
 * returned data with whatever card layout / grid they want.
 */
export function useResourceLibrary({
  initialPosts,
  initialHasMore,
  initialCategory,
  initialSearch,
}: UseResourceLibraryArgs) {
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());
  const [activeCategories, setActiveCategories] = useState<Set<string>>(() =>
    initialCategory ? new Set([initialCategory]) : new Set(),
  );
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? '');
  // Seed the debounced value too so the first render already reflects the
  // deep-linked search without waiting for the debounce timer.
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch ?? '');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  // Seed from the server-rendered first page so the cards are present in the
  // initial HTML (crawlable) and there's no skeleton flash on load.
  const [posts, setPosts] = useState<PostData[]>(initialPosts);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(
    initialPosts.length === 0,
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  // True until the first client-side fetch runs. When the server already
  // provided the first page, we skip that initial fetch (filters are still at
  // their defaults) but allow every subsequent filter/search/sort change to
  // fetch normally.
  const skipInitialFetchRef = useRef(initialPosts.length > 0);

  // Listen for type filter events from ResourcesCenter tags
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { type: string | null };
      if (detail.type === null) {
        setActiveTypes(new Set());
      } else {
        setActiveTypes(new Set([detail.type]));
      }
    };
    window.addEventListener('resource-type-filter', handler);
    return () => window.removeEventListener('resource-type-filter', handler);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchQuery.trim();
      setDebouncedSearch(
        trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH
          ? trimmed
          : '',
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isSearchActive = debouncedSearch.length > 0;

  // When a search is active we always order by newest (the other sort options
  // are disabled in the UI). Derive the effective value during render instead of
  // mutating state in an effect.
  const effectiveSortBy: SortOption = isSearchActive ? 'newest' : sortBy;

  const buildQueryString = useCallback(
    (pageNum: number) => {
      const params = new URLSearchParams();
      if (activeTypes.size > 0) {
        params.set('types', Array.from(activeTypes).join(','));
      }
      if (activeCategories.size > 0) {
        params.set('categories', Array.from(activeCategories).join(','));
      }
      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }
      params.set('sort', effectiveSortBy);
      params.set('page', String(pageNum));
      params.set('pageSize', String(PAGE_SIZE));
      return params.toString();
    },
    [activeTypes, activeCategories, debouncedSearch, effectiveSortBy],
  );

  const fetchPosts = useCallback(
    (pageNum: number, append = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Show the loading state in an async callback so this is not a synchronous
      // setState when fetchPosts is invoked from an effect.
      if (!append) {
        queueMicrotask(() => setLoading(true));
      }

      fetch(`/api/blog/filter?${buildQueryString(pageNum)}`, {
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch');
          }
          return res.json();
        })
        .then((data: ApiResponse) => {
          setPosts((prev) => {
            // Dedupe by post.id. Pagination + featured-first sorting and an
            // in-flight racing fetch can otherwise cause the same document to
            // appear twice, which surfaces as a React duplicate-key warning.
            const next = append ? [...prev, ...data.posts] : data.posts;
            const seen = new Set<string>();
            return next.filter((p) => {
              if (seen.has(p.id)) {
                return false;
              }
              seen.add(p.id);
              return true;
            });
          });
          setHasMore(data.hasMore);
          setPage(pageNum);
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          console.error('Error fetching posts:', err);
        })
        .finally(() => {
          setLoading(false);
          setInitialLoading(false);
        });
    },
    [buildQueryString],
  );

  useEffect(() => {
    // Skip the very first fetch when the server already seeded the default
    // page; re-run normally whenever filters/search/sort change.
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    fetchPosts(1, false);
  }, [fetchPosts]);

  const toggleType = useCallback((type: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const toggleCategory = useCallback((uid: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setActiveTypes(new Set());
    setActiveCategories(new Set());
    setSearchQuery('');
    setDebouncedSearch('');
    setSortBy('relevant');
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) {
      return;
    }
    setLoading(true);
    fetchPosts(page + 1, true);
  }, [hasMore, loading, page, fetchPosts]);

  return {
    // Data
    posts,
    hasMore,
    loading,
    initialLoading,
    // Filters
    activeTypes,
    activeCategories,
    toggleType,
    toggleCategory,
    clearAll,
    // Search
    searchQuery,
    setSearchQuery,
    isSearchActive,
    // Sort
    sortBy,
    setSortBy,
    effectiveSortBy,
    // Pagination
    loadMore,
  };
}
